import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Alert from "@mui/material/Alert";
import { Html5Qrcode } from "html5-qrcode";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

export default function QRScanner({ open, onClose, onScan }) {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    if (!open) {
      stopScanning();
      return;
    }

    // Esperar a que el DOM esté listo antes de inicializar
    const timer = setTimeout(() => {
      const element = document.getElementById("qr-reader");
      if (element && !html5QrCodeRef.current) {
        html5QrCodeRef.current = new Html5Qrcode("qr-reader");
        startScanning();
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      stopScanning();
    };
  }, [open]);

  const startScanning = async () => {
    try {
      setError(null);
      setScanning(true);

      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
      };

      await html5QrCodeRef.current.start(
        { facingMode: "environment" },
        config,
        (decodedText) => {
          handleScanSuccess(decodedText);
        },
        (errorMessage) => {
          // Error de escaneo normal, no hacer nada
        }
      );
    } catch (err) {
      console.error("Error al iniciar scanner:", err);
      setError("No se pudo acceder a la cámara. Verifica los permisos.");
      setScanning(false);
    }
  };

  const stopScanning = async () => {
    if (html5QrCodeRef.current && scanning) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
        setScanning(false);
      } catch (err) {
        console.error("Error al detener scanner:", err);
        setScanning(false);
      }
    }
  };

  const handleScanSuccess = async (decodedText) => {
    await stopScanning();
    onScan(decodedText);
    onClose();
  };

  const handleClose = async () => {
    await stopScanning();
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.clear();
      } catch (err) {
        console.error("Error al limpiar scanner:", err);
      }
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="h5" fontWeight="medium">
            Escanear Código QR
          </MDTypography>
          <IconButton onClick={handleClose} size="small">
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        <MDBox py={2}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <MDBox
            id="qr-reader"
            sx={{
              width: "100%",
              "& video": {
                borderRadius: "8px",
                width: "100% !important",
              },
            }}
          />
          <MDBox mt={2} textAlign="center">
            <MDTypography variant="caption" color="text">
              Apunta la cámara al código QR del producto
            </MDTypography>
          </MDBox>
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="outlined" color="secondary" onClick={handleClose}>
          Cancelar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

QRScanner.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onScan: PropTypes.func.isRequired,
};
